import { uploadFileDirectly } from '../lib/direct-upload';
import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';
import { toExclusionInputs } from './profile-exclusions';

export interface ProfileSummary {
  uuid: string;
  slug?: string | null;
  title: string;
  coverageRadius: number;
  firstName?: string | null;
  lastName?: string | null;
  description?: string | null;
  avatarUrl?: string | null;
  contacts?: ProfileContact[];
  cities?: ProfileCity[];
  socialLinks?: ProfileSocialLink[];
  categories?: ProfileCategory[];
  languages?: ProfileLanguage[];
}

export type ProfileServicePriceMode = 'FIXED' | 'FROM' | 'RANGE';
export type ProfileServicePriceCurrency =
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'AUD'
  | 'CAD'
  | 'CHF'
  | 'CNY'
  | 'JPY'
  | 'PLN'
  | 'UAH';
export type ProfileServicePriceUnit =
  | 'HOUR'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'SESSION'
  | 'ITEM'
  | 'PROJECT'
  | 'CUSTOM';

export interface ProfileSkill {
  uuid: string;
  key: string;
  title: string;
}

export interface ProfileLanguage {
  uuid: string;
  key: string;
  title: string;
}

export interface ProfileCategory {
  uuid: string;
  key: string;
  title: string;
}

export type ProfileContactType = 'PHONE' | 'EMAIL' | 'OTHER';

export const PROFILE_CONTACT_TYPE_OPTIONS: Array<{
  value: ProfileContactType;
  label: string;
}> = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'OTHER', label: 'Other' },
];

export interface ProfileContact {
  uuid: string;
  type: ProfileContactType;
  value: string;
}

export interface ProfileContactInput {
  type: ProfileContactType;
  value: string;
}

export interface ProfileCity {
  uuid: string;
  placeId: string;
  label: string;
}

export interface ProfileCityInput {
  placeId: string;
  label: string;
}

export type ProfileSocialPlatform =
  | 'TWITTER_X'
  | 'INSTAGRAM'
  | 'THREADS'
  | 'FACEBOOK'
  | 'TIKTOK'
  | 'YOUTUBE'
  | 'WEBSITE'
  | 'OTHER';

export const PROFILE_SOCIAL_PLATFORM_OPTIONS: Array<{
  value: ProfileSocialPlatform;
  label: string;
}> = [
  { value: 'TWITTER_X', label: 'Twitter/X' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'THREADS', label: 'Threads' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'OTHER', label: 'Other' },
];

export interface ProfileSocialLink {
  uuid: string;
  platform: ProfileSocialPlatform;
  url: string;
}

export interface ProfileSocialLinkInput {
  platform: ProfileSocialPlatform;
  url: string;
}

export interface ProfileServiceSummary {
  uuid: string;
  title: string;
  shortDescription?: string | null;
  seoDescription?: string | null;
  description?: string | null;
  priceMode: ProfileServicePriceMode;
  priceMin: number;
  priceCurrency: ProfileServicePriceCurrency;
  priceMax?: number | null;
  priceUnit?: ProfileServicePriceUnit | null;
  priceUnitCustom?: string | null;
}

// An Exclusion is either a catalog Skill or the Provider's own wording.
export interface ProfileExclusion {
  uuid?: string;
  title: string;
  skill: ProfileSkill | null;
}

export type ProfileExclusionInput = { skillUuid: string } | { title: string };

export interface ProfileDetails extends ProfileSummary {
  createdAt: string;
  updatedAt: string;
  account: {
    uuid: string;
    username: string;
  };
  skills: ProfileSkill[];
  exclusions: ProfileExclusion[];
  languages: ProfileLanguage[];
  services: ProfileServiceSummary[];
}

export interface CreateProfileInput {
  accountUuid: string;
  title: string;
  coverageRadius?: number;
  firstName?: string;
  lastName?: string;
  description?: string;
  contacts?: ProfileContactInput[];
  cities?: ProfileCityInput[];
  socialLinks?: ProfileSocialLinkInput[];
  categories?: ProfileCategory[];
  skills?: ProfileSkill[];
  languages?: ProfileLanguage[];
}

export interface UpdateProfileInput {
  uuid: string;
  title: string;
  coverageRadius?: number;
  slug?: string | null;
  firstName?: string;
  lastName?: string;
  description?: string;
  contacts?: ProfileContactInput[];
  cities?: ProfileCityInput[];
  socialLinks?: ProfileSocialLinkInput[];
  categories?: ProfileCategory[];
  skills?: ProfileSkill[];
  exclusions?: ProfileExclusion[];
  languages?: ProfileLanguage[];
}

interface CreateProfileMutationInput
  extends Omit<CreateProfileInput, 'cities'> {
  profileCities?: ProfileCityInput[];
}

interface UpdateProfileMutationInput
  extends Omit<UpdateProfileInput, 'cities' | 'exclusions'> {
  profileCities?: ProfileCityInput[];
  exclusions?: ProfileExclusionInput[];
}

export interface UploadProfileAvatarInput {
  file: File;
  profileUuid: string;
}

export interface UploadProfileAvatarResult {
  profileUuid: string;
  avatarUrl?: string | null;
}

export const PROFILE_SLUG_TAKEN_ERROR_CODE = 'PROFILE_SLUG_TAKEN';
export const PROFILE_EXCLUSION_INVALID_ERROR_CODE = 'PROFILE_EXCLUSION_INVALID';
export const PROFILE_EXCLUSION_SKILL_CONFLICT_ERROR_CODE =
  'PROFILE_EXCLUSION_SKILL_CONFLICT';

const GRAPHQL_URL =
  process.env.REACT_APP_API_GRAPHQL_URL ?? 'http://localhost:3000/graphql';

function resolveApiBaseUrl() {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/+$/, '');
  }

  return GRAPHQL_URL.replace(/\/graphql\/?$/, '');
}

const API_BASE_URL = resolveApiBaseUrl();
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function executeProfileRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Profile',
  });
}

function sanitizeProfileSkills(skills?: ProfileSkill[]): ProfileSkill[] | undefined {
  if (!skills) {
    return undefined;
  }

  return skills.map((skill) => ({
    uuid: skill.uuid,
    key: skill.key,
    title: skill.title,
  }));
}

function sanitizeProfileLanguages(
  languages?: ProfileLanguage[]
): ProfileLanguage[] | undefined {
  if (!languages) {
    return undefined;
  }

  return languages.map((language) => ({
    uuid: language.uuid,
    key: language.key,
    title: language.title,
  }));
}

function sanitizeProfileCategories(
  categories?: ProfileCategory[]
): ProfileCategory[] | undefined {
  if (!categories) {
    return undefined;
  }

  return categories.map((category) => ({
    uuid: category.uuid,
    key: category.key,
    title: category.title,
  }));
}

function normalizeLanguageUuids(languageUuids: string[]) {
  return Array.from(
    new Set(
      languageUuids
        .map((languageUuid) => languageUuid.trim().toLowerCase())
        .filter((languageUuid) => UUID_PATTERN.test(languageUuid))
    )
  );
}

function normalizeCategoryUuids(categoryUuids: string[]) {
  return Array.from(
    new Set(
      categoryUuids
        .map((categoryUuid) => categoryUuid.trim().toLowerCase())
        .filter((categoryUuid) => UUID_PATTERN.test(categoryUuid))
    )
  );
}

function sanitizeProfileContacts(
  contacts?: ProfileContactInput[]
): ProfileContactInput[] | undefined {
  if (!contacts) {
    return undefined;
  }

  return contacts.map((contact) => ({
    type: contact.type,
    value: contact.value,
  }));
}

function sanitizeProfileCitys(
  cities?: ProfileCityInput[]
): ProfileCityInput[] | undefined {
  if (!cities) {
    return undefined;
  }

  return cities.map((city) => ({
    placeId: city.placeId,
    label: city.label,
  }));
}

function sanitizeProfileSocialLinks(
  socialLinks?: ProfileSocialLinkInput[]
): ProfileSocialLinkInput[] | undefined {
  if (!socialLinks) {
    return undefined;
  }

  return socialLinks.map((socialLink) => ({
    platform: socialLink.platform,
    url: socialLink.url,
  }));
}

function sanitizeCreateProfileInput(
  input: CreateProfileInput
): CreateProfileMutationInput {
  return {
    accountUuid: input.accountUuid,
    title: input.title,
    firstName: input.firstName,
    lastName: input.lastName,
    description: input.description,
    contacts: sanitizeProfileContacts(input.contacts),
    socialLinks: sanitizeProfileSocialLinks(input.socialLinks),
    categories: sanitizeProfileCategories(input.categories),
    skills: sanitizeProfileSkills(input.skills),
    languages: sanitizeProfileLanguages(input.languages),
  };
}

function sanitizeUpdateProfileInput(
  input: UpdateProfileInput
): UpdateProfileMutationInput {
  return {
    uuid: input.uuid,
    title: input.title,
    slug: input.slug,
    firstName: input.firstName,
    lastName: input.lastName,
    description: input.description,
    contacts: sanitizeProfileContacts(input.contacts),
    socialLinks: sanitizeProfileSocialLinks(input.socialLinks),
    categories: sanitizeProfileCategories(input.categories),
    skills: sanitizeProfileSkills(input.skills),
    exclusions: toExclusionInputs(input.exclusions),
    languages: sanitizeProfileLanguages(input.languages),
  };
}

const PROFILE_SUMMARY_SELECTION_SET = `
          uuid
          slug
          title
          firstName
          lastName
          description
          avatarUrl
          contacts {
            uuid
            type
            value
          }
          socialLinks {
            uuid
            platform
            url
          }
          categories {
            uuid
            key
            title
          }
`;

const PROFILE_DETAILS_SELECTION_SET = `
          uuid
          slug
          title
          firstName
          lastName
          description
          avatarUrl
          contacts {
            uuid
            type
            value
          }
          socialLinks {
            uuid
            platform
            url
          }
          categories {
            uuid
            key
            title
          }
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
          exclusions {
            uuid
            title
            skill {
              uuid
              key
              title
            }
          }
          languages {
            uuid
            key
            title
          }
          services {
            uuid
            title
            shortDescription
            seoDescription
            description
            priceMode
            priceMin
            priceCurrency
            priceMax
            priceUnit
            priceUnitCustom
          }
`;

const ACCOUNT_PROFILES_QUERY = `
      query AccountProfiles($accountUuid: ID!) {
        accountProfiles(accountUuid: $accountUuid) {
${PROFILE_SUMMARY_SELECTION_SET}
        }
      }
    `;

const PROFILE_QUERY = `
      query Profile($uuid: ID!) {
        profile(uuid: $uuid) {
${PROFILE_DETAILS_SELECTION_SET}
        }
      }
    `;

export async function fetchAccountProfiles(
  accountUuid: string,
  accessToken?: string
): Promise<ProfileSummary[]> {
  const response = await executeProfileRequest<{ accountProfiles: ProfileSummary[] }>(
    ACCOUNT_PROFILES_QUERY,
    { accountUuid },
    accessToken
  );

  return response.accountProfiles;
}

export function readCachedAccountProfiles(
  accountUuid: string
): ProfileSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ accountProfiles: ProfileSummary[] }>(
    ACCOUNT_PROFILES_QUERY,
    { accountUuid }
  );

  return cachedData?.accountProfiles ?? (cachedData ? [] : undefined);
}

export async function createProfileRequest(
  input: CreateProfileInput,
  accessToken?: string
): Promise<ProfileSummary> {
  const sanitizedInput = sanitizeCreateProfileInput(input);

  const response = await executeProfileRequest<{ createProfile: ProfileSummary }>(
    `
      mutation CreateProfile($input: CreateProfileInput!) {
        createProfile(input: $input) {
          uuid
          slug
          title
          firstName
          lastName
          description
          avatarUrl
          contacts {
            uuid
            type
            value
          }
          socialLinks {
            uuid
            platform
            url
          }
          categories {
            uuid
            key
            title
          }
        }
      }
    `,
    { input: sanitizedInput },
    accessToken
  );

  return response.createProfile;
}

export async function searchLanguageSkills(
  query: string,
  locale: string,
  accessToken?: string
): Promise<ProfileSkill[]> {
  const normalizedQuery = query.trim();
  if (!normalizedQuery.length) {
    return [];
  }

  const response = await executeProfileRequest<{ searchSkills: ProfileSkill[] }>(
    `
      query SearchSkills($query: String!, $locale: String!, $limit: Int!) {
        searchSkills(query: $query, locale: $locale, limit: $limit) {
          uuid
          key
          title
        }
      }
    `,
    {
      query: normalizedQuery,
      locale,
      limit: 20,
    },
    accessToken
  );

  return response.searchSkills;
}

export async function searchLanguages(
  query: string,
  locale: string,
  accessToken?: string
): Promise<ProfileLanguage[]> {
  const normalizedQuery = query.trim();
  if (!normalizedQuery.length) {
    return [];
  }

  const response = await executeProfileRequest<{ searchLanguages: ProfileLanguage[] }>(
    `
      query SearchLanguages($query: String!, $locale: String!, $limit: Int!) {
        searchLanguages(query: $query, locale: $locale, limit: $limit) {
          uuid
          key
          title
        }
      }
    `,
    {
      query: normalizedQuery,
      locale,
      limit: 20,
    },
    accessToken
  );

  return response.searchLanguages;
}

export async function searchRelatedLanguages(
  selectedLanguageUuids: string[],
  locale: string,
  accessToken?: string
): Promise<ProfileLanguage[]> {
  const normalizedSelectedLanguageUuids = normalizeLanguageUuids(selectedLanguageUuids);
  if (!normalizedSelectedLanguageUuids.length) {
    return [];
  }

  const response = await executeProfileRequest<{ relatedLanguages: ProfileLanguage[] }>(
    `
      query RelatedLanguages($selectedLanguageUuids: [ID!]!, $locale: String!, $limit: Int!) {
        relatedLanguages(
          selectedLanguageUuids: $selectedLanguageUuids
          locale: $locale
          limit: $limit
        ) {
          uuid
          key
          title
        }
      }
    `,
    {
      selectedLanguageUuids: normalizedSelectedLanguageUuids,
      locale,
      limit: 12,
    },
    accessToken
  );

  return response.relatedLanguages;
}

export async function fetchLanguages(
  locale: string,
  limit: number,
  offset: number,
  accessToken?: string
): Promise<ProfileLanguage[]> {
  const response = await executeProfileRequest<{ languages: ProfileLanguage[] }>(
    `
      query Languages($locale: String!, $limit: Int!, $offset: Int!) {
        languages(locale: $locale, limit: $limit, offset: $offset) {
          uuid
          key
          title
        }
      }
    `,
    {
      locale,
      limit,
      offset,
    },
    accessToken
  );

  return response.languages;
}

export async function searchRelatedSkillsByCategories(
  selectedCategoryUuids: string[],
  locale: string,
  accessToken?: string
): Promise<ProfileSkill[]> {
  const normalizedSelectedCategoryUuids = normalizeCategoryUuids(selectedCategoryUuids);
  if (!normalizedSelectedCategoryUuids.length) {
    return [];
  }

  const response = await executeProfileRequest<{
    relatedSkillsByCategories: ProfileSkill[];
  }>(
    `
      query RelatedSkillsByCategories(
        $selectedCategoryUuids: [ID!]!
        $locale: String!
        $limit: Int!
      ) {
        relatedSkillsByCategories(
          selectedCategoryUuids: $selectedCategoryUuids
          locale: $locale
          limit: $limit
        ) {
          uuid
          key
          title
        }
      }
    `,
    {
      selectedCategoryUuids: normalizedSelectedCategoryUuids,
      locale,
      limit: 20,
    },
    accessToken
  );

  return response.relatedSkillsByCategories;
}

export async function fetchCategories(
  locale: string,
  limit: number,
  offset: number,
  accessToken?: string
): Promise<ProfileCategory[]> {
  const response = await executeProfileRequest<{ categories: ProfileCategory[] }>(
    `
      query Categories($locale: String!, $limit: Int!, $offset: Int!) {
        categories(locale: $locale, limit: $limit, offset: $offset) {
          uuid
          key
          title
        }
      }
    `,
    {
      locale,
      limit,
      offset,
    },
    accessToken
  );

  return response.categories;
}

export async function searchCategories(
  query: string,
  locale: string,
  accessToken?: string
): Promise<ProfileCategory[]> {
  const normalizedQuery = query.trim();
  if (!normalizedQuery.length) {
    return [];
  }

  const response = await executeProfileRequest<{ searchCategories: ProfileCategory[] }>(
    `
      query SearchCategories($query: String!, $locale: String!, $limit: Int!) {
        searchCategories(query: $query, locale: $locale, limit: $limit) {
          uuid
          key
          title
        }
      }
    `,
    {
      query: normalizedQuery,
      locale,
      limit: 20,
    },
    accessToken
  );

  return response.searchCategories;
}

export async function fetchCities(
  locale: string,
  limit: number,
  offset: number,
  accessToken?: string
): Promise<ProfileCity[]> {
  const response = await executeProfileRequest<{ cities: ProfileCity[] }>(
    `
      query Cities($locale: String!, $limit: Int!, $offset: Int!) {
        cities(locale: $locale, limit: $limit, offset: $offset) {
          uuid
          placeId
          label
        }
      }
    `,
    {
      locale,
      limit,
      offset,
    },
    accessToken
  );

  return response.cities;
}

export async function searchCities(
  query: string,
  locale: string,
  accessToken?: string
): Promise<ProfileCity[]> {
  const normalizedQuery = query.trim();
  if (!normalizedQuery.length) {
    return [];
  }

  const response = await executeProfileRequest<{ searchCities: ProfileCity[] }>(
    `
      query SearchCities($query: String!, $locale: String!, $limit: Int!) {
        searchCities(query: $query, locale: $locale, limit: $limit) {
          uuid
          placeId
          label
        }
      }
    `,
    {
      query: normalizedQuery,
      locale,
      limit: 20,
    },
    accessToken
  );

  return response.searchCities;
}

export async function updateProfileRequest(
  input: UpdateProfileInput,
  accessToken?: string
): Promise<ProfileDetails> {
  const sanitizedInput = sanitizeUpdateProfileInput(input);

  const response = await executeProfileRequest<{ updateProfile: ProfileDetails }>(
    `
      mutation UpdateProfile($input: UpdateProfileInput!) {
        updateProfile(input: $input) {
          uuid
          slug
          title
          firstName
          lastName
          description
          avatarUrl
          contacts {
            uuid
            type
            value
          }
          socialLinks {
            uuid
            platform
            url
          }
          categories {
            uuid
            key
            title
          }
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
          exclusions {
            uuid
            title
            skill {
              uuid
              key
              title
            }
          }
          languages {
            uuid
            key
            title
          }
          services {
            uuid
            title
            shortDescription
            seoDescription
            description
            priceMode
            priceMin
            priceCurrency
            priceMax
            priceUnit
            priceUnitCustom
          }
        }
      }
    `,
    { input: sanitizedInput },
    accessToken
  );

  return response.updateProfile;
}

export async function fetchProfileByUuid(
  uuid: string,
  accessToken?: string
): Promise<ProfileDetails | null> {
  const response = await executeProfileRequest<{ profile: ProfileDetails | null }>(
    PROFILE_QUERY,
    { uuid },
    accessToken
  );

  return response.profile;
}

export function readCachedProfileByUuid(
  uuid: string
): ProfileDetails | null | undefined {
  const cachedData = readCachedGraphqlQuery<{ profile: ProfileDetails | null }>(
    PROFILE_QUERY,
    { uuid }
  );

  if (!cachedData) {
    return undefined;
  }

  return cachedData.profile;
}

export async function uploadProfileAvatarRequest(
  input: UploadProfileAvatarInput,
  accessToken?: string
): Promise<UploadProfileAvatarResult> {
  const result = await uploadFileDirectly<UploadProfileAvatarResult>({
    accessToken,
    file: input.file,
    routeUrl: `${API_BASE_URL}/profile/${encodeURIComponent(input.profileUuid)}/media`,
  });

  return {
    profileUuid: result.profileUuid,
    avatarUrl: result.avatarUrl ?? null,
  };
}
